import { etToday, formatDate, sentenceCase, weekdayName } from '../lib/format.js';
import { DEFAULT_PERSONA } from '../lib/personas.js';
import { usePersonaPulse } from '../lib/use-persona-pulse.js';
import { Eyebrow } from './Section.jsx';

// The front-page lead (design review 2026-09-26, finding 1). Until then this
// was a navy gradient card with a 72px numeral, ten pips and pill chips, the
// one dark panel on a paper page, and it set the day's actual headline at 20px
// regular under a number. The review's call: the headline leads, the score
// becomes a small ruled "ear" beside it the way a newspaper's weather box sits
// beside its nameplate, and the dayparts become a ruled list. What the score
// says is unchanged: the number, the verdict table and the colour banding.

// Deterministic referent for the score so "7/10" answers "of what?" without
// waiting on the generator. Indexed by score value.
const VERDICTS = [
  'a day to stay in',
  'a day to stay in',
  'a day for plan B',
  'an indoors-leaning day',
  'a day to plan around',
  'a mixed day, pick your windows',
  'a decent day with caveats',
  'a good day to be outside',
  'a strong day to be out',
  'a day to clear the calendar',
  'as good as it gets here'
];

// Band the meter's fill to the score so color and verdict agree: a stormy 4
// must not glow the same affirmative gold as a clear-sky 9. cream-500 is the
// brand's "good"/revenue accent (the Stay underline, the active pill), so it's
// reserved for genuinely good days; never a traffic-light dashboard, never the
// pink alert color.
//
// The ramp must stay MONOTONIC in warmth: rich gold → pale gold → cool blue,
// so a bad day drains of gold rather than picking up a different one. On paper
// the middle step moved from cream-300 to cream-400, because cream-300 is
// 1.49:1 against cream-50 and nearly vanished. Against cream-50: cream-500
// 2.45:1, cream-400 1.94:1, navy-400 3.79:1, empty navy-200. The pips are
// aria-hidden and the numeral carries the value, so this is about being seen,
// not an AA requirement.
function meterFill(value) {
  if (value >= 7) return 'bg-cream-500';
  if (value >= 5) return 'bg-cream-400';
  return 'bg-navy-400';
}

export default function DailyScore({
  score,
  date,
  isToday = true,
  kicker: kickerOverride,
  // False when the plan below carries the picks as its daypart subheads
  // (src/lib/plan.js, item C1). The list here is then the fallback for briefs
  // that do not meet the plan's three-paragraph contract.
  showPicks = true,
  personaTopPicks,
  personaLabel,
  personaId
}) {
  // Highlights the picks list on a persona switch; see usePersonaPulse.
  const pulse = usePersonaPulse(personaLabel);

  if (!score) return null;
  const value = Math.max(0, Math.min(10, Math.round(Number(score.value) || 0)));
  // Archive pages pass their own kicker ("From the archive"). The live page
  // says "Today's brief", or names the weekday between midnight and the
  // morning publish, when it is honestly still showing yesterday's edition.
  // Then the kicker's note says when the new one lands. That line used to sit
  // under the masthead ("You're reading Saturday's brief. Sunday's lands by
  // 6:30 AM ET."), right above a kicker already reading "Saturday's brief".
  const rolled = !kickerOverride && !!date && !isToday;
  const kicker = kickerOverride || (rolled ? `${weekdayName(date)}'s brief` : "Today's brief");
  const kickerNote = rolled ? `${weekdayName(etToday())}'s lands by 6:30 AM ET` : null;
  const fill = meterFill(value);
  // Per-persona topPicks land with briefs generated after 2026-06-12; older
  // briefs only carry the shared score.topPicks, so fall back to those.
  const topPicks = personaTopPicks || score.topPicks;
  // Current briefs send topPicks as { morning, afternoon, evening } (each
  // nullable). Render ALL THREE dayparts whenever the object is present so a
  // storm-blocked (null) window reads as a deliberate "keep it loose" call
  // rather than a forgotten row — the empty window IS the planning signal.
  // Archived briefs before 2026-06-11 send topPick as one
  // "moment · moment · moment" string; keep rendering those as a flat list.
  const hasWindows = !!topPicks && ['morning', 'afternoon', 'evening'].some((w) => topPicks[w]);
  const windows = hasWindows && showPicks
    ? [
        ['Morning', topPicks.morning],
        ['Afternoon', topPicks.afternoon],
        ['Evening', topPicks.evening]
      ]
    : [];
  const legacyMoments = hasWindows || !showPicks
    ? []
    : (score.topPick || '')
        .split('·')
        .map((s) => s.trim())
        .filter(Boolean);
  const drivers = Array.isArray(score.drivers) ? score.drivers.filter(Boolean) : [];

  // The persona tag earns its place only once the guest has narrowed away from
  // the default; "Briefed for: Everyone" on first paint is an unexplained tag
  // asserting an identity the guest never picked.
  const showPersonaTag =
    !!personaLabel && !!personaTopPicks && !!personaId && personaId !== DEFAULT_PERSONA;

  // The tag doubles as the handle for its own control: the PersonaToggle lives
  // below the lead, so connect cause and effect by jumping to it.
  function scrollToToggle() {
    const el = document.getElementById('tailor-brief');
    if (!el) return;
    // An explicit behavior option beats the CSS scroll-behavior the
    // reduced-motion block sets, so the preference has to be read here.
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
    el.querySelector('[aria-pressed="true"]')?.focus();
  }

  const picksHeader = (
    <div className="flex items-center gap-3">
      {/* h2, not h3: the only heading above this is the page h1. Gold small
          caps like every section kicker, since it heads a part of the page. */}
      <h2 className="shrink-0 text-xs font-medium uppercase tracking-[0.18em] text-cream-600">
        Top picks
      </h2>
      <div className="h-px flex-1 bg-navy-100" />
      {showPersonaTag && (
        // -my-3 py-3: tap padding the negative margins hand back, as on the
        // masthead nav, so an 11px label still has a finger-sized target.
        <button
          type="button"
          onClick={scrollToToggle}
          aria-label={`Briefed for ${personaLabel}. Change guest type.`}
          className="-my-3 shrink-0 py-3 text-[11px] font-medium uppercase tracking-[0.16em] text-cream-600 no-underline transition-colors hover:text-navy-900"
        >
          Briefed for: {personaLabel} <span aria-hidden="true">›</span>
        </button>
      )}
    </div>
  );

  return (
    <section aria-labelledby="lead-kicker" className="pb-4 pt-4 sm:pb-8 sm:pt-7">
      <div className="sm:grid sm:grid-cols-[minmax(0,1fr)_176px] sm:gap-9">
        <div>
          {/* Names the section landmark through aria-labelledby. A paragraph,
              not a heading: it sits above the page's h1 in DOM order, and the
              h1 below must stay the first heading on the page. */}
          <Eyebrow id="lead-kicker" text={kicker} note={kickerNote} />

          {/* The daily headline is the page's h1, set at the size the guides
              and About give theirs; the sr-only prefix gives crawlers the
              place + date relevance the visible headline omits. 38px at sm+,
              not 44: beside the ear the column is 460px, and at 44 a
              nine-word headline broke into three choppy lines ("Sunny 82,
              light / NW breeze, full / moon tonight"). At 38 it takes two. */}
          <h1 className="text-balance font-display text-[30px] font-semibold leading-[1.12] tracking-tight text-navy-900 sm:text-[38px] sm:leading-[1.12]">
            {date && (
              <span className="sr-only">{`Charleston conditions for ${formatDate(date)}: `}</span>
            )}
            {score.headline}
          </h1>

          {/* One line, not pill chips: a deck under the headline. Roman, not
              italic, and navy-700: on 2026-09-27 the first screen had four
              italic lines and only one line of plain reading text. The
              model writes drivers in lower case, as chips; a deck opens with
              a capital. */}
          {drivers.length > 0 && (
            <p className="mt-2 font-serif text-[17px] leading-snug text-navy-700 sm:mt-3 sm:text-lg">
              {sentenceCase(drivers.join(' · '))}
            </p>
          )}
        </div>

        {/* The ear. On phones a ruled row under the headline; at sm+ a column
            beside it, the kicker reordered above the numeral. The numeral is
            hidden from screen readers and the kicker carries the value in
            words; aria-hidden sits on each child, never on the sm:contents
            wrapper, because browsers have dropped display:contents elements
            (and their aria-hidden) from the accessibility tree. */}
        <aside className="mt-4 flex items-center gap-4 border-y border-navy-900 py-3 sm:mt-1 sm:flex-col sm:items-start sm:gap-0 sm:self-start sm:py-4">
          <div aria-hidden="true" className="flex shrink-0 items-baseline sm:order-2 sm:mt-1">
            <span className="font-display text-[46px] font-semibold leading-none text-navy-900 sm:text-[52px]">
              {value}
            </span>
            <span className="ml-1 font-display text-lg text-navy-500 sm:text-xl">/10</span>
          </div>
          <div className="min-w-0 sm:contents">
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-cream-600 sm:order-1">
              Outdoor score<span className="sr-only">{`: ${value} out of 10.`}</span>
            </p>
            <div aria-hidden="true" className="mt-1.5 flex gap-[3px] sm:order-3 sm:mt-3">
              {Array.from({ length: 10 }, (_, i) => (
                <span
                  key={i}
                  className={['h-2 w-2 sm:h-2.5 sm:w-2.5', i < value ? fill : 'bg-navy-200'].join(' ')}
                />
              ))}
            </div>
            <p className="mt-1.5 text-balance font-serif text-[15px] leading-snug text-navy-700 sm:order-4 sm:mt-2">
              {sentenceCase(VERDICTS[value])}
            </p>
          </div>
        </aside>
      </div>

      {/* The pulse + changed picks are invisible to screen-reader and
          reduced-motion users; announce the swap politely. Live regions only
          fire on mutation, so the default first paint stays silent. */}
      <p className="sr-only" role="status" aria-live="polite">
        {showPersonaTag ? `Brief now tailored for ${personaLabel}` : ''}
      </p>

      {windows.length > 0 && (
        <div className="mt-5 sm:mt-8">
          {picksHeader}
          {/* Stacked and ruled on phones, three columns with vertical rules
              at sm+. Labels are navy small caps, not gold: gold is reserved for
              structural kickers (the rule UpcomingWeek.jsx records). The picks
              are short editorial lines, so they take the prose face. */}
          <dl
            className={[
              '-mx-2 mt-1 divide-y divide-navy-100 px-2 transition-colors duration-300 motion-reduce:transition-none sm:mt-4 sm:grid sm:grid-cols-3 sm:divide-x sm:divide-y-0 sm:py-1',
              pulse ? 'bg-cream-100' : 'bg-transparent'
            ].join(' ')}
          >
            {windows.map(([label, text], i) => (
              <div
                key={label}
                className={['py-3 sm:py-0', ['sm:pr-6', 'sm:px-6', 'sm:pl-6'][i]].join(' ')}
              >
                <dt className="text-[11px] font-semibold uppercase tracking-[0.16em] text-navy-500">
                  {label}
                </dt>
                {/* A null window stays in the list as a quiet "your call" line
                    so the daypart reads as deliberate, never a dropped row. */}
                <dd
                  className={[
                    'mt-1 font-serif text-[17px] leading-snug',
                    text ? 'text-navy-900' : 'italic text-navy-500'
                  ].join(' ')}
                >
                  {text ? sentenceCase(text) : 'Open, your call.'}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {legacyMoments.length > 0 && (
        <div className="mt-5 sm:mt-8">
          {picksHeader}
          <ul className="mt-1 divide-y divide-navy-100">
            {legacyMoments.map((moment, i) => (
              <li key={i} className="py-3 font-serif text-[17px] leading-snug text-navy-900">
                {moment}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
